<template>
  <el-col :span="props.spanColumn">
    <div class="label">
      {{ props.label }}
      <el-tooltip
        v-if="settings.tooltip"
        :content="settings.tooltip"
        :trigger="['hover', 'click']"
        placement="top"
        popper-class="custom-input-number-tooltip"
      >
        <IconInformationOutline class="info-icon" />
      </el-tooltip>
    </div>
    <el-row style="margin-bottom: 0">
      <el-input-number
        ref="inputNumberRef"
        v-model="value"
        :placeholder="settings.placeholder"
        :class="settings.inputClass"
        :size="settings.size"
        :disabled="settings.disabled"
        :min="settings.min"
        :max="settings.max"
        :precision="settings.precision"
        :controls="settings.controls"
        :step="settings.step"
        :formatter="formatNumber"
        :parser="parseNumber"
        @focus="handleFocus"
        @blur="handleBlur"
      ></el-input-number>
      <span
        v-if="props.unit"
        class="unit"
        >{{ props.unit }}</span
      >
    </el-row>
  </el-col>
</template>

<script setup>
import {
  computed,
  ref,
  nextTick,
} from 'vue';
import {
  formatNumberLocale,
  parseNumberLocale,
  getLocaleSeparators,
} from '@/shared/utils/locale_number_utils.js';
import IconInformationOutline from '~icons/mdi/information-outline';

//props
const props = defineProps({
  spanColumn: Number,
  label: String,
  unit: [String, undefined],
  settings: {
    type: Object,
    default: () => ({}),
  },
  modelValue: {
    type: [String, Number, Boolean],
  },
});

const settings = computed(() => ({
  placeholder: '',
  inputClass: '',
  disabled: false,
  min: -Infinity,
  max: Infinity,
  precision: null,
  controls: true,
  step: 1,
  size: 'default',
  tooltip: '',
  ...props.settings,
}));

const emit = defineEmits(['update:modelValue', 'blur']);

const value = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const formatNumber = (val) => {
  if (isFocused.value) {
    const { decimalSeparator } = getLocaleSeparators();
    return String(val ?? '').replace('.', decimalSeparator);
  }
  return formatNumberLocale(val, settings.value.precision ?? undefined);
};
const parseNumber = (val) => {
  const result = parseNumberLocale(val);
  return result !== undefined ? String(result) : '';
};

const isFocused = ref(false);
const inputNumberRef = ref(null);

const handleFocus = () => {
  isFocused.value = true;
  nextTick(() => {
    const input = inputNumberRef.value?.$el?.querySelector('input');
    if (input && props.modelValue != null) {
      const { decimalSeparator } = getLocaleSeparators();
      input.value = String(props.modelValue).replace('.', decimalSeparator);
    }
  });
};

const shouldRestoreMin = () => {
  const isControlsEnabled = settings.value.controls === true;
  if (!isControlsEnabled) return false;

  const minValue = Number(settings.value.min);
  if (!Number.isFinite(minValue)) return false;

  return props.modelValue === '' || props.modelValue === null || props.modelValue === undefined;
};

const handleBlur = () => {
  isFocused.value = false;
  nextTick(() => {
    const input = inputNumberRef.value?.$el?.querySelector('input');
    if (input) {
      input.value = formatNumberLocale(props.modelValue, settings.value.precision ?? undefined);
    }
  });
  if (shouldRestoreMin()) {
    emit('update:modelValue', Number(settings.value.min));
  }
  emit('blur');
};
</script>

<style scoped>
.label {
  font-weight: 700;
  color: #111;
}

.info-icon {
  margin-left: 4px;
  vertical-align: middle;
  color: #909399;
  cursor: pointer;
}

.el-input:deep(div.el-input__wrapper) {
  border-radius: 10px;
}

.el-input:deep(div.el-input__wrapper.is-focus) {
  outline: 2px var(--emagen-black-greenish) inset;
}

.unit {
  margin-left: 8px;
  color: var(--emagen-black-greenish);
  font-weight: bold;
}

.ancho_total {
  width: 100%;
}
</style>

<style>
.custom-input-number-tooltip {
  max-width: 260px;
  font-size: 12px;
  line-height: 1.5;
  white-space: normal;
}
</style>
